import { useParams } from 'react-router-dom';

import { useMarketsContext } from '../MarketContext';

import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { AssetInfo, AssetMetrics } from '../types';
import { ProfitLoss } from '../../ProfitLoss';
import { computePercentage } from '../../../helpers/functions';
import { useState } from 'react';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import { IAsset } from '../../../types/model';
import { GET_ASSET } from '../../../http/apollo-queries';

const currency = 'usd';

type Table = 'overview' | 'metrics' | 'financials' | 'dividents';

export const MetricsWidget = ({ table }: { table: Table }) => {
  const { id = '' } = useParams();
  const { assetsList } = useMarketsContext();

  const { data: { getAsset } = {} } = useQuery<{ getAsset: IAsset }>(
    GET_ASSET,
    {
      variables: { id },
      skip: !id,
    }
  );

  const asset =
    assetsList.find(({ id: assetId }) => assetId === id) ?? getAsset;

  const {
    country,
    info: assetInfo,
    history,
    marketCap: assetMarketCap,
    name,
    currency: assetCurrency,
    type: assetType,
  } = asset!;

  const {
    address,
    CEO,
    categories,
    classificationData,
    coupon,
    currency: infoCurrency,
    cusip,
    description,
    employees,
    etfData,
    exchange,
    general,
    industry,
    isin,
    issueData,
    lastTradeDate,
    links,
    marketData: {
      ath,
      atl,
      circulatingSupply,
      currentPrice,
      fullyDilutedValuation,
      high24h,
      low24h,
      marketCap,
      maxSupply,
      priceChangePercentage24hInCurrency,
      priceChangePercentage7dInCurrency,
      totalSupply,
      totalVolume,
    } = {},
    maturityDate,
    meta,
    mutualFundData,
    nextCallDate,
    rating,
    statistics: {
      dividendsAndSplits,
      financials,
      stockPriceSummary,
      stockStatistics,
      valuationsMetrics,
    } = {},
    symbol,
    technicals,
    type,
    updateDate,
    website,
    wkn,
    yieldToMaturity,
  } = JSON.parse(assetInfo?.data ?? '{}') as AssetInfo;

  const price = currentPrice?.[currency] ?? history[0]?.cur;
  const rapVolumeMarketCap =
    totalVolume?.[currency] && marketCap?.[currency]
      ? totalVolume[currency] / marketCap[currency]
      : undefined;

  const assetMetrics: AssetMetrics = {
    // METRICS
    ...(table === 'metrics' && {
      // CRYPTO
      price: { value: price, suffix: assetCurrency },
      '24H change': {
        value:
          priceChangePercentage24hInCurrency?.[currency] ??
          computePercentage(
            history[1]?.[currency] ?? 0,
            history[0]?.[currency] ?? 0,
            100
          ),
        suffix: '%',
      },
      '24H high': { value: high24h?.[currency], suffix: '$' },
      '24H low': { value: low24h?.[currency], suffix: '$' },
      '24H trading volume': { value: totalVolume?.[currency] },
      '7D change': {
        value:
          priceChangePercentage7dInCurrency?.[currency] ??
          computePercentage(
            history[6]?.[currency] ?? 0,
            history[0]?.[currency] ?? 0,
            100
          ),
        suffix: '%',
      },
      'all time high': { value: ath?.[currency], suffix: '$' },
      'all time low': { value: atl?.[currency], suffix: '$' },
      'circulating supply': { value: circulatingSupply },
      'fully diluted valuation': {
        value: fullyDilutedValuation?.[currency],
        suffix: '$',
      },
      // 'market cap in USD': {
      //   value: assetMarketCap,
      //   suffix: '$',
      // },
      'max supply': { value: maxSupply },
      'total supply': { value: totalSupply },
      'volume / market cap': { value: rapVolumeMarketCap },

      // STOCK
      'market capitalization in currency': {
        // + ETF
        value: valuationsMetrics?.marketCapitalization,
        suffix: assetCurrency,
      },
      'enterprise value': {
        value: valuationsMetrics?.enterpriseValue,
        suffix: assetCurrency,
      },
      'trailing pe': { value: valuationsMetrics?.trailingPe },
      'forward pe': { value: valuationsMetrics?.forwardPe },
      'peg ratio': { value: valuationsMetrics?.pegRatio },
      'price to sales ttm': {
        value: financials?.incomeStatement?.revenueTtm,
        suffix: assetCurrency,
      },
      'price to book mrq': {
        value: valuationsMetrics?.priceToBookMrq,
        suffix: assetCurrency,
      },
      'enterprise to revenue': {
        value: valuationsMetrics?.enterpriseToRevenue,
        suffix: assetCurrency,
      },
      'enterprise to ebitda': {
        value: valuationsMetrics?.enterpriseToEbitda,
        suffix: assetCurrency,
      },
      'shares outstanding': {
        value: stockStatistics?.sharesOutstanding,
      },
      'float shares': { value: stockStatistics?.floatShares },
      'avg 10 volume': { value: stockStatistics?.avg10Volume },
      'avg 30 volume': { value: stockStatistics?.avg30Volume },
      'shares short': { value: stockStatistics?.sharesShort },
      'short ratio': { value: stockStatistics?.shortRatio },
      'short percent of shares outstanding': {
        value: stockStatistics?.shortPercentOfSharesOutstanding,
        suffix: '%',
      },
      'percent held by insiders': {
        value: stockStatistics?.percentHeldByInsiders,
        suffix: '%',
      },
      'percent held by institutions': {
        value: stockStatistics?.percentHeldByInstitutions,
        suffix: '%',
      },
      'fifty two week low': {
        value: stockPriceSummary?.fiftyTwoWeekLow,
        suffix: assetCurrency,
      },
      'fifty two week high': {
        value: stockPriceSummary?.fiftyTwoWeekHigh,
        suffix: assetCurrency,
      },
      'fifty two week change': {
        value: stockPriceSummary?.fiftyTwoWeekChange,
        suffix: assetCurrency,
      },
      beta: {
        value: stockPriceSummary?.beta ?? technicals?.beta,
        suffix: assetCurrency,
      }, // + ETF
      'day 50 ma': { value: stockPriceSummary?.day50MA },
      'day 200 ma': { value: stockPriceSummary?.day200MA },

      // BOND
      'update date': { value: updateDate ?? mutualFundData?.updateDate }, // + FUNDS
      'last trade date': { value: lastTradeDate },
      'yield to maturity': { value: yieldToMaturity },
      'next call date': { value: nextCallDate },
      // 'minimum settlement': { value:  },
      // 'amount': { value:  },
      // 'par integral multiple': { value:  },
      // 'classification data': { value: classificationData. },
      'bond type': { value: classificationData?.bondType },
      'debt type': { value: classificationData?.debtType },
      'moody rating': { value: rating?.moodyRating },
      'moody rating update date': { value: rating?.moodyRatingUpdateDate },
      'S&P rating': { value: rating?.spRating },
      'S&P rating update date': { value: rating?.spRatingUpdateDate },
      'offering date': { value: issueData?.offeringDate },
      'first coupon date': { value: issueData?.firstCouponDate },
      'first trading day': { value: issueData?.firstTradingDay },
      'coupon payment frequency': { value: issueData?.couponPaymentFrequency },

      // ETF
      '52 week high': { value: technicals?.['52WeekHigh'] },
      '52 week low': { value: technicals?.['52WeekLow'] },
      '50 day MA': { value: technicals?.['50DayMa'] },
      '200 day MA': { value: technicals?.['200DayMa'] },
      'current yield': { value: etfData?.yield },
      'dividend paying frequency': { value: etfData?.dividendPayingFrequency },
      // 'coupon payment frequency': { value:  }, is on BOND
      'holdings count': { value: etfData?.holdingsCount },
      'morning star/ratio': { value: etfData?.morningStar?.ratio },
      'morning star/category benchmark': {
        value: etfData?.morningStar?.categoryBenchmark,
      },
      'morning star/sustainability ratio': {
        value: etfData?.morningStar?.sustainabilityRatio,
      },
      'market capitalization mega': {
        value: etfData?.marketCapitalisation?.mega,
      },
      'market capitalization big': {
        value: etfData?.marketCapitalisation?.big,
      },
      'market capitalization medium': {
        value: etfData?.marketCapitalisation?.medium,
      },
      'market capitalization small': {
        value: etfData?.marketCapitalisation?.small,
      },
      'market capitalization micro': {
        value: etfData?.marketCapitalisation?.micro,
      },

      // FUNDS
      nav: { value: mutualFundData?.nav },
      yield: { value: mutualFundData?.yield },
      'inception date': { value: mutualFundData?.inceptionDate },
    }),

    // OVERVIEW
    ...(table === 'overview' && {
      // COMMON
      symbol: { value: symbol ?? general?.code }, // + ETF
      website: {
        // + ETF
        value: website ?? links?.homepage?.[0] ?? etfData?.companyUrl,
      },
      description: {
        // ++ ETF + FUNDS
        value:
          description?.en ??
          description ??
          general?.description ??
          general?.fundSummary,
      },

      // CRYPTO
      ecosystems: {
        value: categories ? Object.values(categories).join(', ') : categories,
      },

      // STOCK
      // +FUNDS + ETF
      exchange: { value: exchange ?? meta?.exchange ?? general?.exchange },
      timezone: { value: meta?.exchangeTimezone },
      industry: { value: industry ?? classificationData?.industryGroup }, // + BOND
      employees: { value: employees },
      type: { value: type }, // + FUNDS
      CEO: { value: CEO },
      adress: { value: address },

      // BOND
      currency: {
        // +ETF + FUNDS
        value:
          assetCurrency ?? general?.currencyName ?? mutualFundData?.currency,
      },
      issuer: { value: issueData?.issuer },
      'issuer website': { value: issueData?.issuerUrl },
      'issue data': { value: issueData?.issueDate },
      coupon: { value: coupon },
      maturityDate: { value: maturityDate },
      rating: { value: rating?.spRating },
      isin: { value: isin ?? etfData?.isin ?? general?.isin }, // + ETF + FUNDS
      cusip: { value: cusip ?? general?.cusip }, // + FUNDS
      wkn: { value: wkn },

      // ETF
      company: { value: etfData?.companyName },
      category: { value: general?.category },
      country: { value: general?.countryName ?? country }, // + BOND + FUNDS
      'ETF URL': { value: etfData?.etfUrl },

      // FOREX
      ...(assetType === 'FOREX' && {
        AVG30D: {
          value:
            history.slice(0, 29).reduce((a, b) => a + b[currency], 0) /
            history.slice(0, 29).length,
        },
        YTH: {
          value: Math.max(
            ...history
              .slice(
                0,
                history.findIndex(
                  ({ date }) => moment(date).format('MM-DD') === '01-01'
                )
              )
              .map(({ [currency]: val }) => val)
          ),
        },
      }),
    }),

    // FINANCIALS
    ...(table === 'financials' && {
      // STOCK
      'most recent quarter': { value: financials?.mostRecentQuarter },
      'profit margin': {
        value: financials?.profitMargin,
        suffix: assetCurrency,
      },
      'operating margin': {
        value: financials?.operatingMargin,
        suffix: assetCurrency,
      },
      'return on assets ttm': {
        value: financials?.returnOnAssetsTtm,
        suffix: assetCurrency,
      },
      'return on equity ttm': {
        value: financials?.returnOnEquityTtm,
        suffix: assetCurrency,
      },
      'revenue income ttm': {
        value: financials?.incomeStatement?.revenueTtm,
        suffix: assetCurrency,
      },
      'revenue per share ttm': {
        value: financials?.incomeStatement?.revenuePerShareTtm,
        suffix: assetCurrency,
      },
      'quarterly revenue growth': {
        value: financials?.incomeStatement?.quarterlyRevenueGrowth,
        suffix: assetCurrency,
      },
      'gross profit ttm': {
        value: financials?.incomeStatement?.grossProfitTtm,
        suffix: assetCurrency,
      },
      ebitda: {
        value: financials?.incomeStatement?.ebitda,
        suffix: assetCurrency,
      },
      'net income to common ttm': {
        value: financials?.incomeStatement?.netIncomeToCommonTtm,
        suffix: assetCurrency,
      },
      'diluted eps ttm': {
        value: financials?.incomeStatement?.dilutedEpsTtm,
        suffix: assetCurrency,
      },
      'quarterly earnings growth yoy': {
        value: financials?.incomeStatement?.quarterlyEarningsGrowthYoy,
        suffix: assetCurrency,
      },
      'revenue balance ttm': {
        value: financials?.balanceSheet?.revenueTtm,
        suffix: assetCurrency,
      },
      'total cash mrq': {
        value: financials?.balanceSheet?.totalCashMrq,
        suffix: assetCurrency,
      },
      'total cash per share mrq': {
        value: financials?.balanceSheet?.totalCashPerShareMrq,
        suffix: assetCurrency,
      },
      'total debt mrq': {
        value: financials?.balanceSheet?.totalDebtMrq,
        suffix: assetCurrency,
      },
      'total debt to equity mrq': {
        value: financials?.balanceSheet?.totalDebtToEquityMrq,
        suffix: assetCurrency,
      },
      'current ratio mrq': {
        value: financials?.balanceSheet?.currentRatioMrq,
        suffix: assetCurrency,
      },
      'book value per share mrq': {
        value: financials?.balanceSheet?.bookValuePerShareMrq,
        suffix: assetCurrency,
      },
      'operating cash flow ttm': {
        value: financials?.cashFlow?.operatingCashFlowTtm,
        suffix: assetCurrency,
      },
      'levered free cash flow ttm': {
        value: financials?.cashFlow?.leveredFreeCashFlowTtm,
        suffix: assetCurrency,
      },

      // ETF
      '1y volatility': { value: etfData?.performance?.['1YVolatility'] },
      '3y volatility': { value: etfData?.performance?.['3YVolatility'] },
      '3y expReturn': { value: etfData?.performance?.['3YExpReturn'] },
      '3y sharpRatio': { value: etfData?.performance?.['3YSharpRatio'] },
      'returns YTD': { value: etfData?.performance?.returnsYtd },
      'returns 1Y': { value: etfData?.performance?.returns1Y },
      'returns 3Y': { value: etfData?.performance?.returns3Y },
      'returns 5Y': { value: etfData?.performance?.returns5Y },
      'returns 10Y': { value: etfData?.performance?.returns10Y },
    }),

    // DIVIDENTS
    ...(table === 'dividents' && {
      // STOCK
      'forward annual dividend rate': {
        value: dividendsAndSplits?.forwardAnnualDividendRate,
      },
      'forward annual dividend yield': {
        value: dividendsAndSplits?.forwardAnnualDividendYield,
      },
      'trailing annual dividend rate': {
        value: dividendsAndSplits?.trailingAnnualDividendRate,
      },
      'trailing annual dividend yield': {
        value: dividendsAndSplits?.trailingAnnualDividendYield,
      },
      '5 year average dividend yield': {
        value: dividendsAndSplits?.['5YearAverageDividendYield'],
      },
      'payout ratio': { value: dividendsAndSplits?.payoutRatio },
      'dividend date': { value: dividendsAndSplits?.dividendDate },
      'ex dividend date': { value: dividendsAndSplits?.exDividendDate },
      'last split factor': { value: dividendsAndSplits?.lastSplitFactor },
      'last split date': { value: dividendsAndSplits?.lastSplitDate },
    }),
  };

  const metrics = Object.entries(assetMetrics).reduce(
    (arr, [metric, { value, suffix }]) =>
      typeof value === 'number' ||
      (typeof value === 'string' && value.length > 0)
        ? [...arr, { metric, value, suffix }]
        : arr,
    [] as { metric: string; value: number | string; suffix?: string }[]
  );

  const [canSeeDescription, setCanSeeDescription] = useState(false);

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-grey-90">
      <div
        className={`
          flex
          w-full
          items-center
          pl-5
          py-3
          border-b
          border-grey-10
          dark:border-grey-70
        `}
      >
        <span className="flex items-center gap-2">
          <div className={'flex mx-2'}>
            <div
              className={`
                border-1
                border-grey-10
                rounded-2xl
                p-2
                flex
                items-center
                text-xs
                font-semibold
                cursor-default
                uppercase
              `}
            >
              <div className={`bg-orange w-3 h-3 rounded-full mx-2`} />

              <span className="pr-2">{name}</span>
            </div>
          </div>
        </span>
      </div>

      <div
        className={`
          divide-y-1
          divide-grey-10
          dark:divide-grey-70
          overflow-auto
          text-left
        `}
      >
        <div className={'inline-grid grid-cols-2 p-2 w-full capitalize'}>
          {['KEY METRIC', 'VALUE'].map((columnName, j) => (
            <div
              key={columnName}
              className={`
                flex
                items-center
                justify-left
                pl-5
                font-medium
                py-3
              `}
            >
              {columnName}
            </div>
          ))}
        </div>
        <div className="flex-col">
          {metrics.map(({ metric, suffix, value }) => (
            <div
              key={metric + value}
              className={`
                inline-grid
                grid-cols-${
                  canSeeDescription && metric === 'description' ? 1 : 2
                }
                pl-9
                py-3
                items-center
                w-full
                border-b
                border-grey-10
                dark:border-grey-70
                ${metric === 'description' && 'cursor-pointer'}
                ${
                  metric === 'description' &&
                  !canSeeDescription &&
                  'hover:bg-grey-5'
                }
              `}
              onClick={() =>
                metric === 'description' && setCanSeeDescription((pr) => !pr)
              }
            >
              {metric !== 'description' ? (
                <div className={'flex  text-grey-50 capitalize'}>{metric}</div>
              ) : (
                !canSeeDescription && (
                  <div className={'flex text-grey-50 capitalize'}>{metric}</div>
                )
              )}
              {typeof value === 'string' ? (
                <div
                  className={`
                    ${
                      ['website', 'URL'].some((el) => metric.includes(el)) &&
                      'text-blue cursor-pointer hover:text-blue-dark-1'
                    }
                  `}
                  onClick={() =>
                    ['website', 'URL'].some((el) => metric.includes(el)) &&
                    window.open(value, '_blank', 'noopener,noreferrer')
                  }
                >
                  {metric === 'description' && !canSeeDescription
                    ? value.slice(0, 34) + '...'
                    : value}
                </div>
              ) : suffix === '%' ? (
                <ProfitLoss
                  displayType="text"
                  percentageClassName="text-md"
                  percentage={+value}
                />
              ) : (
                <CustomNumber
                  displayType="text"
                  value={+value}
                  suffix={suffix ? ' ' + suffix?.toLocaleUpperCase() : ''}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
